import { ReactElement, useState } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { ReactComponent as LubavLogoWithText } from '@backrs_ui/common-ui/src/assets/images/lubav-logo-with-text.svg';
import { ReactComponent as LubavLogo } from '@backrs_ui/common-ui/src/assets/images/lubav-logo.svg';
import { ReactComponent as ProsperLogo } from '@backrs_ui/common-ui/src/assets/images/prosper-logo.svg';
import { BackrsIcons } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Box, Drawer, List, SxProps, Theme } from '@mui/material';

import { PageShellNavMenuItem } from './PageShellNavMenuItem';

const DRAWER_WIDTH_COLLAPSED = 80;
const DRAWER_WIDTH_EXPANDED = 260;

type MenuItem = Readonly<{
  label: string;
  icon: BackrsIcons;
  iconWidth?: number;
  rightIcon?: BackrsIcons;
  isActive: boolean;
  onClick: () => void;
  onRightIconClick?: () => void;
}>;

type BackrsPageShellWrapperProps = Readonly<{
  application: 'LUBAV' | 'PROSPER';
  onApplicationLogoClick: () => void;
  children: ReactElement;
  topMenuItems: MenuItem[];
  bottomMenuItems: MenuItem[];
}>;

export const BackrsPageShellWrapper = ({
  application,
  onApplicationLogoClick,
  children,
  topMenuItems,
  bottomMenuItems,
}: BackrsPageShellWrapperProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const prosperLogo = <ProsperLogo />;
  const lubavLogo = isOpen ? <LubavLogoWithText /> : <LubavLogo />;

  const isProsper = application === 'PROSPER';

  return (
    <>
      <Drawer
        variant="permanent"
        onMouseOver={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        sx={getDrawerStyle(isOpen)}
      >
        <Box>
          {/* APPLICATION LOGO */}
          {/* the icons are different sizes for Prosper and Lubav, so we adjust accordingly */}
          <Box ml={isProsper ? 3 : 2} sx={{ cursor: 'pointer' }} onClick={onApplicationLogoClick}>
            {isProsper && prosperLogo}
            {!isProsper && lubavLogo}
          </Box>

          {/* TOP MENU ITEMS */}
          {!!topMenuItems?.length && (
            <List>
              {topMenuItems.map((item) => (
                <PageShellNavMenuItem
                  key={item.label}
                  label={item.label}
                  leftIcon={{
                    icon: item.icon,
                    iconColor: item.isActive ? appColors.color.blue1 : appColors.color.blue2,
                    alt: item.label,
                  }}
                  isActive={item.isActive}
                  isExpanded={isOpen}
                  onClick={item.onClick}
                />
              ))}
            </List>
          )}
        </Box>

        {/* BOTTOM MENU ITEMS */}
        <List sx={{ mb: 3 }}>
          {bottomMenuItems.map((item) => (
            <PageShellNavMenuItem
              key={item.label}
              label={item.label}
              leftIcon={{
                icon: item.icon,
                alt: item.label,
                iconWidth: item.iconWidth,
              }}
              rightIcon={
                item.rightIcon && {
                  icon: item.rightIcon,
                  alt: item.label,
                  onRightIconClick: item.onRightIconClick,
                }
              }
              isExpanded={isOpen}
              onClick={item.onClick}
            />
          ))}
        </List>
      </Drawer>

      {/* PAGE CONTENT */}
      <Box sx={childrenContainerStyle}>{children}</Box>
    </>
  );
};

const getDrawerStyle = (isOpen: boolean): SxProps<Theme> => ({
  width: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
  backgroundColor: appColors.color.taupe1,
  '& .MuiDrawer-paper': {
    justifyContent: 'space-between',
    display: 'flex',
    width: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
    borderWidth: 0,
    overflowX: 'hidden',
    backgroundColor: appColors.color.taupe1,
    transition: `width ${isOpen ? '300ms ease-out' : '75ms ease-in'}`,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.14)',
    pt: 2,
  },
});

const childrenContainerStyle: SxProps<Theme> = {
  flexGrow: 1,
  marginLeft: `${DRAWER_WIDTH_COLLAPSED}px`,
} as const;
